<script setup lang="ts">
/**
 * La barra laterale.
 *
 * §10.6: si riduce a icone e ricorda la scelta. Ridotta mostra solo il telaio,
 * aperta mostra il lockup — e la regola non è estetica, è che il logotipo a 20
 * pixel di altezza diventa una macchia grigia, il che è esattamente ciò che
 * `minHeight()` calcola nel pacchetto marchio.
 *
 * Le voci si filtrano per permesso. Non è sicurezza — quella la fa il server a
 * ogni richiesta — è che un menu con dodici voci di cui otto rispondono «non
 * autorizzato» non è un menu: è un elenco di frustrazioni. Una sezione le cui
 * voci sono tutte nascoste sparisce con la sua intestazione, altrimenti
 * resterebbe un titolo sospeso sopra il vuoto.
 */

import { computed } from 'vue';
import { NAV, voceVisibile } from '@/config/navigation';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import BesteLogo from '@/components/brand/BesteLogo.vue';
import GissiLabLogo from '@/components/brand/GissiLabLogo.vue';
import { PanelLeftClose, PanelLeftOpen } from '@lucide/vue';

const auth = useAuthStore();

/** Chi lavora a Gissi vede GissiLab: il nome del disegno lo manda il server con la sede. */
const gissilab = computed(() => auth.user?.headquarter?.brand === 'gissilab');
const ui = useUiStore();

/** Le sezioni con almeno una voce visibile, già ripulite dalle voci invisibili. */
const sezioni = computed(() =>
  NAV.map((sezione) => ({
    ...sezione,
    // La condizione non si riscrive qui: la tiene `voceVisibile`, che e' l'unico padrone
    // del fatto «questa voce si vede». Scritta in due posti, il 14 ottobre, i due posti
    // dicevano cose diverse e la home offriva una voce che il menu nascondeva (§116).
    voci: sezione.voci.filter((voce) => voceVisibile(voce.name, auth)),
  })).filter((sezione) => sezione.voci.length > 0),
);
</script>

<template>
  <aside
    class="barra"
    :class="{ 'barra--ridotta': ui.barraRidotta, 'barra--aperta-mobile': ui.barraMobileAperta }"
    aria-label="Navigazione principale"
  >
    <!--
      Il marchio è anche il collegamento al cruscotto: è la convenzione che tutti
      conoscono, e risparmia una voce di menu.
    -->
    <RouterLink :to="{ name: 'cruscotto' }" class="marchio" aria-label="BesteHR — vai al cruscotto">
      <!--
        A barra ridotta resta il telaio di Beste anche a Gissi: GissiLab non ha un simbolo da
        solo, e il suo logotipo intero in quella larghezza scenderebbe sotto la leggibilità.
      -->
      <GissiLabLogo v-if="gissilab && !ui.barraRidotta" :height="32" />
      <BesteLogo v-else-if="!ui.barraRidotta" variant="lockup" :height="34" />
      <BesteLogo v-else variant="mark" :height="30" />
    </RouterLink>

    <nav class="voci">
      <template v-for="sezione in sezioni" :key="sezione.titolo ?? '_'">
        <!--
          Ridotta, il titolo di sezione non si accorcia: diventa una riga.
          Un'etichetta tagliata a tre lettere non informa nessuno e occupa lo
          stesso spazio verticale di un separatore che invece funziona.
        -->
        <p v-if="sezione.titolo && !ui.barraRidotta" class="sezione">{{ sezione.titolo }}</p>
        <hr v-else-if="sezione.titolo" class="sezione-riga" />

        <RouterLink
          v-for="voce in sezione.voci"
          :key="voce.name"
          v-tooltip.right="ui.barraRidotta ? voce.label : undefined"
          :to="{ name: voce.name }"
          class="voce"
          active-class="voce--attiva"
        >
          <component :is="voce.icon" class="voce-icona" :size="20" :stroke-width="1.75" />
          <!-- `title` per le etichette lunghe, che a barra stretta finiscono
               tagliate dai puntini. A barra ridotta il testo ha `visibility:
               hidden` e il suggerimento del sistema non compare, quindi non si
               somma a quello di PrimeVue qui sopra: i due si alternano invece
               di accavallarsi. -->
          <span class="voce-testo" :title="voce.label">{{ voce.label }}</span>
        </RouterLink>
      </template>
    </nav>

    <button
      type="button"
      class="riduci"
      :aria-expanded="!ui.barraRidotta"
      :aria-label="ui.barraRidotta ? 'Allarga la barra laterale' : 'Riduci la barra laterale'"
      @click="ui.alternaBarra()"
    >
      <component
        :is="ui.barraRidotta ? PanelLeftOpen : PanelLeftClose"
        :size="18"
        :stroke-width="1.75"
      />
      <span class="voce-testo">Riduci</span>
    </button>
  </aside>
</template>

<style scoped>
.barra {
  position: sticky;
  top: 0;
  z-index: var(--beste-z-sticky);

  display: flex;
  flex-direction: column;
  height: 100dvh;

  background: var(--beste-surface-raised);

  /*
   * Il bordo è un `border-inline-end` e non un'ombra: un'ombra su un pannello
   * alto quanto lo schermo è una sfumatura di due pixel replicata per mille
   * pixel di altezza, cioè una linea grigia disegnata nel modo più costoso
   * possibile.
   */
  border-inline-end: 1px solid var(--beste-border-subtle);
  overflow: hidden;
}

.marchio {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--beste-layout-topbar);
  flex: none;
  color: var(--beste-text-primary);
  text-decoration: none;
  border-bottom: 1px solid var(--beste-border-subtle);
}

.voci {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain; /* lo scorrimento del menu non trascina la pagina */
  padding: var(--beste-space-12) var(--beste-space-8) var(--beste-space-8);
}

.sezione {
  margin: var(--beste-space-16) var(--beste-space-12) var(--beste-space-4);
  font-size: var(--beste-font-overline-size);
  line-height: var(--beste-font-overline-line);
  font-weight: var(--beste-font-overline-weight);
  letter-spacing: var(--beste-font-overline-tracking);
  text-transform: uppercase;
  color: var(--beste-text-muted);
  max-width: none; /* le fondamenta limitano la larghezza dei paragrafi: qui è un'etichetta */
}

.sezione-riga {
  margin: var(--beste-space-12) var(--beste-space-12);
  border: 0;
  border-top: 1px solid var(--beste-border-subtle);
}

.voce {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--beste-space-12);

  /*
   * 40 px di altezza, non 48.
   *
   * I 48 px del sistema di progetto sono la misura di un bersaglio **toccato**
   * con un dito. Questa barra vive su un mouse: 40 px sono comodi da colpire,
   * e su venti voci fanno 160 pixel di altezza risparmiati — cioè quattro voci
   * in più visibili senza scorrere su un portatile.
   */
  min-height: 40px;
  padding: 0 var(--beste-space-12);
  margin-bottom: var(--beste-space-2);
  border-radius: var(--beste-radius-md);

  color: var(--beste-text-secondary);
  text-decoration: none;
  font-size: var(--beste-font-body-size);
  white-space: nowrap;

  transition:
    background-color var(--beste-duration-fast) var(--beste-ease-standard),
    color var(--beste-duration-fast) var(--beste-ease-standard);
}

.voce:hover {
  background: var(--beste-surface-subtle);
  color: var(--beste-text-primary);
}

/*
 * Il riscontro alla pressione.
 *
 * `ripple: false` in `main.ts` dice, testualmente, che «il riscontro lo dà lo
 * stato :active». Non era vero da nessuna parte: l'onda di Material era stata
 * tolta e non sostituita, quindi fra il momento in cui si preme e quello in cui
 * la pagina cambia non succedeva niente. Su una connessione lenta sono i
 * duecento millisecondi in cui si pensa di aver mancato il bersaglio e si preme
 * una seconda volta.
 */
.voce:active {
  background: var(--beste-surface-sunken);
  transition-duration: var(--beste-duration-instant);
}

/*
 * `.voce--attiva:hover` esiste per un motivo di cascata, non di gusto.
 *
 * `.voce:hover` pesa (0,2,0) e `.voce--attiva` pesa (0,1,0): passando il mouse
 * sulla voce in cui ci si trova gia', quella vinceva e la voce corrente
 * diventava grigia come tutte le altre. Cioe' l'unico momento in cui si guarda
 * il menu per capire dove si e' era anche l'unico in cui il menu smetteva di
 * dirlo.
 */
.voce--attiva {
  background: var(--beste-accent-surface);
  color: var(--beste-accent-text);
  font-weight: var(--beste-font-bodyStrong-weight);
}

.voce--attiva:hover {
  /* Risponde comunque al passaggio, ma dentro la propria famiglia di colore. */
  background: color-mix(in srgb, var(--beste-accent-surface), var(--beste-accent-solid) 10%);
  color: var(--beste-accent-text);
}

.voce--attiva:active {
  background: color-mix(in srgb, var(--beste-accent-surface), var(--beste-accent-solid) 18%);
}

/*
 * L'indicatore della voce corrente: una barretta d'accento sul bordo sinistro.
 *
 * Esiste perché il colore da solo non basta mai a segnalare uno stato (§1.7 e
 * §12): chi non distingue il bronzo dal grigio vede comunque *una forma* in più.
 * Vale ancora di più adesso, perché il bronzo sta nella stessa famiglia calda
 * dei neutri e si stacca dal grigio meno di quanto se ne staccasse un accento
 * freddo — la barretta è la parte del segnale che non dipende dal colore.
 * Cresce dal centro invece di comparire, il che costa nulla e rende il
 * passaggio da una voce all'altra un movimento invece che un salto.
 */
.voce--attiva::before {
  content: '';
  position: absolute;
  inset-block: 8px;
  inset-inline-start: -8px;
  width: 3px;
  border-radius: var(--beste-radius-full);
  background: var(--beste-accent-solid);
  animation: barretta var(--beste-duration-normal) var(--beste-ease-enter);
}

@keyframes barretta {
  from {
    transform: scaleY(0);
    opacity: 0;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
}

.voce-icona {
  flex: none;
}

/*
 * La transizione dell'etichetta sta **qui**, sulla classe di base, e non sulla
 * classe di stato.
 *
 * Scritta dentro `.barra--ridotta .voce-testo` — com'era — governava solo
 * l'andata: quando la classe veniva tolta, veniva tolta con lei anche la regola
 * che diceva come tornare. La barra si chiudeva con garbo e si riapriva di
 * scatto, e il difetto e' invisibile finche' non si guardano le due direzioni
 * una dopo l'altra.
 *
 * I ritardi sono opposti nei due sensi, ed e' voluto. In apertura lo spazio si
 * libera subito e il testo ci si posa dentro; in chiusura il testo sbiadisce
 * prima e solo dopo lo spazio si richiude. Senza il ritardo in chiusura la
 * larghezza andrebbe a zero al primo fotogramma, il testo sarebbe gia' tagliato
 * via dal contenitore e la dissolvenza si consumerebbe su qualcosa che nessuno
 * sta piu' vedendo.
 */
.voce-testo {
  overflow: hidden;
  text-overflow: ellipsis;
  transition:
    opacity var(--beste-duration-fast) var(--beste-ease-enter),
    width 0s,
    visibility 0s;
}

.riduci {
  display: flex;
  align-items: center;
  gap: var(--beste-space-12);
  flex: none;

  height: 44px;
  padding: 0 var(--beste-space-20);
  border-top: 1px solid var(--beste-border-subtle);

  color: var(--beste-text-muted);
  font-size: var(--beste-font-small-size);
  cursor: pointer;
  white-space: nowrap;

  transition: color var(--beste-duration-fast) var(--beste-ease-standard);
}

.riduci:hover {
  color: var(--beste-text-primary);
  background: var(--beste-surface-subtle);
}

.riduci:active {
  background: var(--beste-surface-sunken);
  transition-duration: var(--beste-duration-instant);
}

/* ------------------------------------------------------------------ ridotta */

/*
 * Le etichette non si nascondono con `display: none`.
 *
 * Sparirebbero di colpo mentre la barra si sta ancora stringendo, e per due
 * decimi di secondo resterebbero delle icone in mezzo al nulla. Con opacità e
 * larghezza a zero il testo si ritira *insieme* alla barra. `visibility` alla
 * fine è ciò che lo toglie davvero al tabulatore e ai lettori di schermo: un
 * testo a opacità zero resta leggibile per loro, ed è un difetto classico.
 */
.barra--ridotta .voce-testo {
  opacity: 0;
  width: 0;
  visibility: hidden;
  transition:
    opacity var(--beste-duration-fast) var(--beste-ease-exit),
    width 0s linear var(--beste-duration-fast),
    visibility 0s linear var(--beste-duration-fast);
}

.barra--ridotta .voce,
.barra--ridotta .riduci {
  justify-content: center;
  padding-inline: 0;
  gap: 0;
}

.barra--ridotta .voce--attiva::before {
  inset-inline-start: 0;
}

/* ------------------------------------------------------------------- mobile */

@media (max-width: 1023px) {
  .barra {
    position: fixed;
    inset-block: 0;
    inset-inline-start: 0;
    width: var(--beste-layout-sidebar-expanded);
    z-index: var(--beste-z-drawer);

    /*
     * Fuori schermo, non nascosta.
     *
     * `transform` invece di `display: none` perché è ciò che permette al
     * pannello di *entrare* scorrendo. E `visibility` accompagna la traslazione
     * per la stessa ragione di sopra: un menu fuori schermo ma visibile resta
     * raggiungibile con il tabulatore, e si finisce a navigare alla cieca
     * dentro qualcosa che non c'è.
     */
    transform: translateX(-100%);
    visibility: hidden;
    box-shadow: var(--beste-elevation-modal);
    transition:
      transform var(--beste-duration-normal) var(--beste-ease-exit),
      visibility 0s linear var(--beste-duration-normal);
  }

  .barra--aperta-mobile {
    transform: none;
    visibility: visible;
    transition:
      transform var(--beste-duration-normal) var(--beste-ease-enter),
      visibility 0s;
  }

  /* Sul telefono la barra è sempre per esteso: ridurla non servirebbe a nulla,
     perché è sovrapposta e non toglie spazio al contenuto. */
  .barra .voce-testo {
    opacity: 1;
    width: auto;
    visibility: visible;
  }

  /*
   * Qui i 40 px tornano 48.
   *
   * La motivazione dei 40 px, scritta sopra, e' che «questa barra vive su un
   * mouse»: sotto i 1024 px non e' piu' vero, ci si arriva con il pollice. E il
   * risparmio verticale che li giustificava non esiste nemmeno, perche' il
   * pannello e' sovrapposto e alto quanto lo schermo. Il token e' esattamente
   * questo caso: il lato minimo di un bersaglio toccato.
   */
  .voce {
    min-height: var(--beste-layout-touchTarget);
  }

  .riduci {
    display: none;
  }
}
</style>
